import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ProgressDialog, ProgressState } from '../../../components/ProgressDialog';
import { AutocompleteAircraft } from '../../Settings/HangarSettings/AutocompleteAircraft';

interface Trip {
  id: number;
  tail_number: string;
  arrival_date: string;
  departure_date: string;
  aircraft_id?: string;
  make?: string;
  model?: string;
  wingspan?: number;
  length?: number;
}

interface SimpleBulkUploadPresenterProps {
  // Dialog state
  open: boolean;
  onClose: () => void;

  // Data state
  trips: Trip[];
  pasteData: string;
  setPasteData: (data: string) => void;
  loading: boolean;
  isImporting?: boolean;
  error: string | null;
  setError: (error: string | null) => void;

  // Computed values
  validCount: number;
  exampleDates: {
    example1: string;
    example2: string;
    example3: string;
    example4: string;
  };

  // Actions
  handleImportData: () => void;
  downloadSampleFile: () => void;
  addRow: () => void;
  removeRow: (id: number) => void;
  updateTrip: (id: number, field: keyof Trip, value: string) => void;
  updateTripAircraft: (id: number, aircraft: any) => void;
  handleSave: () => void;
  handleClose: () => void;
  activeFBOId: string;

  // Auto-assignment info
  autoAssignLocationName?: string;

  // Render mode
  embedded?: boolean; // If true, renders content without Dialog wrapper

  // Progress tracking
  uploadProgress?: ProgressState;
}

export const SimpleBulkUploadPresenter: React.FC<SimpleBulkUploadPresenterProps> = ({
  open,
  onClose,
  trips,
  pasteData,
  setPasteData,
  loading,
  isImporting = false,
  error,
  setError,
  validCount,
  exampleDates,
  handleImportData,
  downloadSampleFile,
  addRow,
  removeRow,
  updateTrip,
  updateTripAircraft,
  handleSave,
  handleClose,
  activeFBOId,
  autoAssignLocationName,
  embedded = false,
  uploadProgress,
}) => {
  // Determine if error is actually a success message
  const isSuccessMessage = error?.startsWith('✓');
  const isPartialSuccess = error?.includes('✓') && error?.includes('✗');

  // Helper function to check if dates are valid (arrival < departure)
  const hasInvalidDates = (trip: Trip): boolean => {
    if (!trip.arrival_date || !trip.departure_date) return false;
    return new Date(trip.arrival_date) >= new Date(trip.departure_date);
  };

  const content = (
    <>
        {/* Show location assignment info if auto-assignment is enabled */}
        {autoAssignLocationName && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Trips will be automatically assigned to <strong>{autoAssignLocationName}</strong>
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Paste from Excel
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copy trip data from Excel (Tail Number, Arrival Date, Departure Date) and paste below.
            For base tenants, leave the departure column blank or use "no departure".
            Headers are optional and will be automatically detected and skipped.
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={pasteData}
            onChange={(e) => setPasteData(e.target.value)}
            placeholder={`${exampleDates.example1}\n${exampleDates.example2}\n${exampleDates.example3}\n${exampleDates.example4}`}
            disabled={isImporting}
            sx={{ mb: 2 }}
          />
          <Button
            variant="outlined"
            onClick={handleImportData}
            disabled={!pasteData.trim() || isImporting}
            startIcon={isImporting ? <CircularProgress size={16} /> : undefined}
            sx={{ mr: 2 }}
          >
            {isImporting ? "Importing..." : "Import Data"}
          </Button>
          <Button
            variant="text"
            onClick={downloadSampleFile}
            sx={{ mr: 2 }}
          >
            Sample File
          </Button>
          {pasteData.trim() && (
            <Typography variant="caption" color="text.secondary">
              {pasteData.trim().split('\n').length} rows ready to import
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>
          Edit Trips
        </Typography>

        {/* Error/Success display */}
        {error && (
          <Alert
            severity={isSuccessMessage && !isPartialSuccess ? "success" : isPartialSuccess ? "warning" : "error"}
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</pre>
          </Alert>
        )}

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tail Number</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>Arrival Date</TableCell>
              <TableCell>Departure Date</TableCell>
              <TableCell align="center">
                <Button size="small" onClick={addRow} startIcon={<AddIcon />}>
                  Add
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trips.map((trip) => (
              <TableRow key={trip.id}>
                <TableCell sx={{ py: 2 }}>
                  <TextField
                    size="small"
                    value={trip.tail_number}
                    onChange={(e) => updateTrip(trip.id, 'tail_number', e.target.value)}
                    placeholder="N123AB"
                    sx={{ width: 120 }}
                  />
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  {trip.aircraft_id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {trip.make}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {trip.model}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <WarningIcon color="warning" fontSize="small" sx={{ flexShrink: 0 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <AutocompleteAircraft
                          label="Select Aircraft"
                          onSelect={(aircraft) => updateTripAircraft(trip.id, aircraft)}
                          defaultSelected={null}
                        />
                      </Box>
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <TextField
                    type="datetime-local"
                    size="small"
                    value={trip.arrival_date}
                    onChange={(e) => updateTrip(trip.id, 'arrival_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={hasInvalidDates(trip)}
                    helperText={hasInvalidDates(trip) ? 'Arrival must be before departure' : ''}
                    sx={{ width: 200 }}
                  />
                </TableCell>
                <TableCell sx={{ py: 2 }}>
                  <TextField
                    type="datetime-local"
                    size="small"
                    value={trip.departure_date}
                    onChange={(e) => updateTrip(trip.id, 'departure_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={hasInvalidDates(trip)}
                    helperText={hasInvalidDates(trip) ? 'Departure must be after arrival' : ''}
                    sx={{ width: 200 }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ py: 2 }}>
                  <IconButton
                    size="small"
                    onClick={() => removeRow(trip.id)}
                    disabled={trips.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
    </>
  );

  // If embedded, just return the content without Dialog wrapper
  if (embedded) {
    return (
      <>
        {content}
        {/* Progress Dialog for Upload Operation */}
        {loading && uploadProgress && uploadProgress.total > 0 && (
          <ProgressDialog
            open={loading}
            title="Uploading Trips"
            message="Creating trips and assigning to location. Please wait..."
            current={uploadProgress.current}
            total={uploadProgress.total}
            itemName="trip"
            currentItem={uploadProgress.currentItem}
          />
        )}
      </>
    );
  }

  // Otherwise, wrap in Dialog (standalone mode)
  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Upload Trips</DialogTitle>
        <DialogContent>
          {content}
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="text.secondary">
            {validCount} valid trips
          </Typography>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={validCount === 0 || loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              `Save ${validCount} Trips`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Dialog for Upload Operation */}
      {loading && uploadProgress && uploadProgress.total > 0 && (
        <ProgressDialog
          open={loading}
          title="Uploading Trips"
          message="Creating trips and assigning to location. Please wait..."
          current={uploadProgress.current}
          total={uploadProgress.total}
          itemName="trip"
          currentItem={uploadProgress.currentItem}
        />
      )}
    </>
  );
};